// Customer facing country selector for the top of the enquiry form

import classNames from "classnames";
import React from "react";
import { connect } from "react-redux";
import { actions } from "react-redux-form";

import { Avatar } from "../../../../core/atoms/atoms";
import { api } from "../../../../shared/api/system/apiClient";

import { CommonProps, EnquiryFormSection } from "./common";

import _ from "underscore";
import "./countrySelect.scss";

interface OwnProps {
  regions: IRegionSummary[];
  countries: ICountrySummary[];
  onRegionSelect?: () => any;
  onCountrySelect?: (country: ICountry) => any;
}

interface DispatchProps {
  dispatch: any;
}

interface SelectorState {
  activeRegionId: number|null;

  /**
   * Theory: someone who clicks on the region labels will find hover behaviour confusing
   */
  clickOnly: boolean;

  regionsWrapHeight: number|null;
}

class CountrySelect extends EnquiryFormSection<OwnProps&DispatchProps, SelectorState> {
  private selectorWrapRef: HTMLElement;
  private regionsWrapRef: HTMLElement;
  private somewhereElseRef: HTMLElement;

  public constructor(props) {
    super(props);
    this.state = {
      activeRegionId: null,
      clickOnly: false,
      regionsWrapHeight: null,
    };
  }

  private resetSelectorScroll = () => {
    requestAnimationFrame(() => {
      this.selectorWrapRef.scrollBy({ behavior: "smooth", left: -this.selectorWrapRef.scrollLeft });
    });
  }

  /**
   * Somewhere else? link doesn't do anything if the selector doesn't scroll - so hide it
   */
  private setSomewhereElseVisibility = () => {
    const selectorIsScrolling = this.selectorWrapRef.scrollWidth > this.selectorWrapRef.clientWidth;
    this.somewhereElseRef.style.display = selectorIsScrolling ? "block" : "none";
  }

  /**
   * Select the region, and put the selector into click only mode
   */
  private onRegionClick = (regionId: number) => {
    this.setState({ activeRegionId: regionId, clickOnly: true }, () => this.props.onRegionSelect && this.props.onRegionSelect());

    // Scroll countries into view; will only be a scroller at small sizes so this is a no-op otherwise
    // behaviour: smooth currently only works in Fx 36+, Chrome 61+
    // TODO add polyfill, replace usages of zenscroll with native call
    requestAnimationFrame(() => {
      const scrollAmt = 200 - this.selectorWrapRef.scrollLeft;
      this.selectorWrapRef.scrollBy({ behavior: "smooth", left: scrollAmt });
      this.setSomewhereElseVisibility();
    });
  }

  /**
   * Select the region only, but don't do the autoscroll over
   */
  private onRegionMouseEnter = (regionId: number) => {
    if (this.state.clickOnly) {
      return;
    }

    this.setState({ activeRegionId: regionId }, () => this.setSomewhereElseVisibility());
  }

  private selectCountry = async (id: number) => {
    const countrySummary = this.props.countries.filter(x => x.id === id)[0];

    try {
      // No need for pending state; since this is the first question
      const response = await api.getLocationBySlug(countrySummary.slug);
      const country = response.json as ICountry;

      await this.props.dispatch(actions.change(this.props.model, country));

      if (this.props.onCountrySelect) {
        this.props.onCountrySelect(country);
      }
    }
    catch (e) {
      this.props.dispatch(actions.setErrors(this.props.model, e.message));
    }
  }

  protected shouldUpdateValidity(oldProps: CommonProps, newProps: CommonProps) {
    return !_.isEqual(oldProps.formState.location, newProps.formState.location);
  }

  protected syncValidity(formState: Forms.EnquiryForm) {
    const hasCountry = formState.location != null; // doesn't get called when the country select is unmounted when selecting a country
    this.props.dispatch(actions.setValidity(this.props.model, hasCountry));
  }

  public componentDidUpdate() {
    this.setSomewhereElseVisibility();

    if (!this.state.regionsWrapHeight || !this.regionsWrapRef || !this.props.regions || this.props.regions.length < 1) {
      return;
    }

    // On first render of regions, bind the countries height to the region height so the country list wraps horizontally
    // More reliable than using CSS absolute wrapper hack
    this.setState({ regionsWrapHeight: this.regionsWrapRef.clientHeight });
  }

  public render() {
    const { regions, countries } = this.props;
    if (!regions || regions.length < 1 || !countries || countries.length < 1) {
      return null;
    }

    const defaultRegionIndex = 1;
    const activeRegionId = this.state.activeRegionId || regions[defaultRegionIndex].id;
    const regionCountries = countries.filter(country => country.parent != null && country.parent.id === activeRegionId);

    const countriesWrapHeight = this.regionsWrapRef
      ? this.regionsWrapRef.clientHeight
      : 355; // roughly correct

    return (
      <section ref={x => this.selectorWrapRef = x} className="country-selector">
        <ul className="regions" ref={x => this.regionsWrapRef = x}>
          {this.props.regions.map((region, index) =>
            <li key={`r-${region.id}`}
                className={classNames({ active: this.state.activeRegionId ? this.state.activeRegionId === region.id : index === defaultRegionIndex })}
                onClick={() => this.onRegionClick(region.id)}
                onMouseEnter={() => this.onRegionMouseEnter(region.id)}>
              {region.name}
            </li>,
          )}
        </ul>
        <ul className={classNames("region-countries", { fewer: regionCountries.length <= 8 })} style={{ height: `${countriesWrapHeight}px` }}>
          {regionCountries.map(country => {
            const thumbnailUrl = country.heroImage ? country.heroImage.thumbnailUrl : null;
            return (
              <li key={`c-${country.id}`} onClick={() => this.selectCountry(country.id)}>
                <Avatar source={thumbnailUrl} />{country.name}
              </li>
            );
          })}

          <li ref={x => this.somewhereElseRef = x} className="somewhere-else" onClick={() => this.resetSelectorScroll()}>
            <Avatar source="/images/missing-image.jpg" />Somewhere else?
          </li>
        </ul>
      </section>
    );
  }
}

export const CountrySelector = connect<OwnProps&CommonProps>()(CountrySelect);
